import React, { useCallback, useEffect, useRef, useState } from "react";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { mergeRegister } from "@lexical/utils";
import {
	SELECTION_CHANGE_COMMAND,
	$getSelection,
	$isRangeSelection,
} from "lexical";

import { Button } from "components";
import { getSelectedNode } from "components/editor/helpers/getSelectedNode";
import { positionEditorElement } from "components/editor/helpers/positionEditorElement";

import styles from "../../editor.module.scss";

const LowPriority = 1;

export function FloatingLinkEditor({ editor }) {
	const editorRef: any = useRef(null);
	const inputRef: any = useRef(null);
	const mouseDownRef = useRef(false);
	const [linkUrl, setLinkUrl] = useState("");
	const [isEditMode, setEditMode] = useState(false);
	const [lastSelection, setLastSelection] = useState(null);

	const updateLinkEditor = useCallback(() => {
		const selection: any = $getSelection();
		if ($isRangeSelection(selection)) {
			const node = getSelectedNode(selection);
			const parent = node.getParent();
			if ($isLinkNode(parent)) {
				setLinkUrl(parent.getURL());
			} else if ($isLinkNode(node)) {
				setLinkUrl(node.getURL());
			} else {
				setLinkUrl("");
			}
		}
		const editorElem = editorRef?.current;
		const nativeSelection = window.getSelection();
		const activeElement = document.activeElement;

		if (editorElem === null) {
			return;
		}

		const rootElement = editor.getRootElement();
		if (
			selection !== null &&
			!nativeSelection?.isCollapsed &&
			rootElement !== null &&
			rootElement.contains(nativeSelection?.anchorNode)
		) {
			const domRange = nativeSelection?.getRangeAt(0);
			let rect;
			if (nativeSelection?.anchorNode === rootElement) {
				let inner = rootElement;
				while (inner.firstElementChild != null) {
					inner = inner.firstElementChild;
				}
				rect = inner.getBoundingClientRect();
			} else {
				rect = domRange?.getBoundingClientRect();
			}

			if (!mouseDownRef.current) {
				positionEditorElement(editorElem, rect);
			}
			setLastSelection(selection);
		} else if (
			!activeElement ||
			activeElement.className !== `${styles.linkInput}`
		) {
			positionEditorElement(editorElem, null);
			setLastSelection(null);
			setEditMode(false);
			setLinkUrl("");
		}

		return true;
	}, [editor]);

	useEffect(() => {
		return mergeRegister(
			editor.registerUpdateListener(({ editorState }) => {
				editorState.read(() => {
					updateLinkEditor();
				});
			}),

			editor.registerCommand(
				SELECTION_CHANGE_COMMAND,
				() => {
					updateLinkEditor();
					return true;
				},
				LowPriority,
			),
		);
	}, [editor, updateLinkEditor]);

	useEffect(() => {
		editor.getEditorState().read(() => {
			updateLinkEditor();
		});
	}, [editor, updateLinkEditor]);

	useEffect(() => {
		if (isEditMode && inputRef?.current) {
			inputRef.current.focus();
		}
	}, [isEditMode]);

	const handleRemoveLink = () => {
		editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
	};

	const handleSaveLink = () => {
		if (lastSelection !== null) {
			if (linkUrl !== "") {
				editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
			}
			setEditMode(false);
		}
	};

	return (
		<div ref={editorRef} className={styles.linkEditor}>
			{isEditMode ? (
				<div className={styles.linkInputWrapper}>
					<input
						ref={inputRef}
						className={styles.linkInput}
						value={linkUrl}
						onChange={event => {
							setLinkUrl(event.target.value);
						}}
						onKeyDown={event => {
							if (event.key === "Enter") {
								event.preventDefault();
								handleSaveLink();
							} else if (event.key === "Escape") {
								event.preventDefault();
								setEditMode(false);
							}
						}}
					/>
					<Button
						onClick={handleSaveLink}
						btnIcon
						variant="secondary"
						size="sm"
						className={styles.linkSave}
						value="Save"
					/>
				</div>
			) : (
				<>
					<div className={styles.linkPreview}>
						<a
							href={linkUrl}
							target="_blank"
							rel="noopener noreferrer"
						>
							{linkUrl}
						</a>
						<Button
							onClick={handleRemoveLink}
							btnIcon
							variant="secondary"
							size="sm"
							className={styles.linkRemove}
							value="Remove"
						/>
						<Button
							onClick={() => setEditMode(true)}
							btnIcon
							variant="secondary"
							size="sm"
							className={styles.linkEdit}
							value="Edit"
						/>
					</div>
				</>
			)}
		</div>
	);
}
